import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  Divider,
  Paper,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DataGrid } from "@mui/x-data-grid";
import api from "../api/client";
import cerdodelosllanosCover from "../assets/images/cerdodelosllanos_cover.jpeg";
import { useTranslation } from "react-i18next";
import { useScreen } from "../hooks/useScreen";
import { useGridHeight } from "../hooks/useGridHeight";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import StatCard from "../components/StatCard";
import AuthContext from "../context/AuthContext";

dayjs.extend(weekOfYear);

export default function DashboardHome() {
  const { t, i18n } = useTranslation();
  const { isXs } = useScreen();
  const { user } = useContext(AuthContext);

  const [counts, setCounts] = useState({ cameras: 0, users: 0, registries: 0 });
  const [registries, setRegistries] = useState([]);
  const [filteredRegistries, setFilteredRegistries] = useState([]);
  const [cameraMap, setCameraMap] = useState({});
  const [dateRange, setDateRange] = useState([null, null]);
  const [groupBy, setGroupBy] = useState("day");
  const [activePreset, setActivePreset] = useState("all");

  useEffect(() => {
    const fetchCountsAndRegistries = async () => {
      try {
        const requests = [
          api.get("/cameras/"),
          user?.is_admin ? api.get("/users/") : Promise.resolve({ data: [] }),
          api.get("/registries/by-client"),
          api.get("/registries/by-client"),
        ];

        const [camsRes, usersRes, regsRes, clientRegs] = await Promise.all(requests);

        const cameras = camsRes.data.reduce((acc, cam) => {
          const base =
            [cam.location, cam.notes].filter(Boolean).join(" - ") ||
            cam.description ||
            cam.name ||
            `Camera ${cam.id}`;
          acc[cam.id] = base;
          return acc;
        }, {});

        setCounts({
          cameras: camsRes.data.length,
          users: usersRes.data.length,
          registries: regsRes.data.length,
        });

        setCameraMap(cameras);
        setRegistries(clientRegs.data);
        setFilteredRegistries(clientRegs.data);
      } catch (err) {
        console.error("Error loading dashboard data:", err);
      }
    };

    fetchCountsAndRegistries();
  }, [user?.is_admin]);

  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) {
      setFilteredRegistries(registries);
      return;
    }
    const [start, end] = dateRange;
    const filtered = registries.filter((r) => {
      const regDate = dayjs(r.start_date);
      return (
        regDate.isAfter(dayjs(start).startOf("day")) &&
        regDate.isBefore(dayjs(end).endOf("day"))
      );
    });
    setFilteredRegistries(filtered);
  }, [dateRange, registries]);

  const formatDate = (value) => {
    const date = value ? new Date(value) : null;
    return date ? date.toLocaleString(i18n.language) : "-";
  };

  const toDayKey = (d) => dayjs(d).format("YYYY-MM-DD");
  const toWeekKey = (d) =>
    `${dayjs(d).year()}-W${String(dayjs(d).week()).padStart(2, "0")}`;

  const keyFn = useMemo(
    () => (groupBy === "week" ? toWeekKey : toDayKey),
    [groupBy]
  );

  const dailyCounts = useMemo(() => {
    const byKey = new Map();
    for (const r of filteredRegistries) {
      const key = keyFn(r.start_date);
      const ai = Number.isFinite(+r.count) ? +r.count : 0;
      const sup = Number.isFinite(+r.supervised_count) ? +r.supervised_count : 0;
      const manual = Number.isFinite(+r.manual_count)
        ? +r.manual_count
        : undefined;

      if (!byKey.has(key))
        byKey.set(key, {
          date: key,
          ai_count: 0,
          supervised_count: 0,
          manual_count: 0,
          has_manual: false,
        });

      const row = byKey.get(key);
      row.ai_count += ai;
      row.supervised_count += sup;
      if (manual !== undefined) {
        row.manual_count += manual;
        row.has_manual = true;
      }
    }
    return Array.from(byKey.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredRegistries, keyFn]);

  const hasAnyManual = useMemo(
    () => dailyCounts.some((d) => d.has_manual && d.manual_count > 0),
    [dailyCounts]
  );

  const { categoryCounts, categoryKeys } = useMemo(() => {
    const byKey = new Map();
    const keys = new Set(["CAP", "MEI", "CACH", "ETC"]);

    for (const r of filteredRegistries) {
      const key = keyFn(r.start_date);
      const cat = (r.pig_sex || "").toUpperCase().trim() || "ETC";
      const amount = Number.isFinite(+r.count)
        ? +r.count
        : Number.isFinite(+r.supervised_count)
        ? +r.supervised_count
        : 0;

      if (!byKey.has(key)) byKey.set(key, { date: key });

      const row = byKey.get(key);
      row[cat] = (row[cat] || 0) + amount;
      keys.add(cat);
    }

    const rows = Array.from(byKey.values()).sort((a, b) => a.date.localeCompare(b.date));
    const finalKeys = Array.from(keys).filter((k) => k !== "date");

    for (const r of rows) {
      for (const k of finalKeys) {
        if (k !== "date" && r[k] == null) r[k] = 0;
      }
    }
    return { categoryCounts: rows, categoryKeys: finalKeys };
  }, [filteredRegistries, keyFn]);

  const kpis = useMemo(() => {
    if (!filteredRegistries.length) return { accuracy: 0, total7: 0, cams7: 0 };

    const sevenDaysAgo = dayjs().subtract(7, "day");
    let accSum = 0;
    let accN = 0;
    let total7 = 0;
    const cams = new Set();

    for (const r of filteredRegistries) {
      const ai = Number.isFinite(+r.count) ? +r.count : null;
      const sup = Number.isFinite(+r.supervised_count) ? +r.supervised_count : null;

      if (ai !== null && sup !== null && sup > 0) {
        const acc = Math.max(0, 1 - Math.abs(ai - sup) / sup);
        accSum += acc;
        accN += 1;
      }

      const d = dayjs(r.start_date);
      if (d.isAfter(sevenDaysAgo)) {
        total7 += Number.isFinite(+r.count)
          ? +r.count
          : Number.isFinite(+r.supervised_count)
          ? +r.supervised_count
          : 0;
        cams.add(r.camera_id);
      }
    }

    return {
      accuracy: accN ? Math.round((accSum / accN) * 100) : 0,
      total7,
      cams7: cams.size,
    };
  }, [filteredRegistries]);

  const gridHeight = useGridHeight(filteredRegistries.length, {
    row: isXs ? 44 : 52,
    header: 56,
    footer: 52,
    max: isXs ? 420 : 600,
  });

  // ✅ Utilidad de formato (opcional)
const fmt = (n) =>
  typeof n === "number" ? n.toLocaleString() : n ?? "—";

  const registryColumns = [
    {
      field: "camera_id",
      headerName: t("camera"),
      width: 220,
      valueGetter: (params) =>
        cameraMap[params] || `Camera ${params ?? "-"}`,
    },
    {
      field: "start_date",
      headerName: t("start"),
      flex: 1,
      valueGetter: (params) => formatDate(params),
    },
    {
      field: "end_date",
      headerName: t("end"),
      flex: 1,
      valueGetter: (params) => formatDate(params),
    },
    { field: "lote_number", headerName: t("lote_number"), width: 120 },
    { field: "exit_room", headerName: t("exit_room"), width: 120 },
    { field: "trip_number", headerName: t("trip_number"), width: 120 },
    {
      field: "pig_sex",
      headerName: t("pig_sex"),
      width: 120,
      renderCell: (params) => {
        if (params.value === "CAP") return t("CAP");
        if (params.value === "MEI") return t("MEI");
        if (params.value === "CACH") return t("CACH");
        return "-";
      },
    },
    { field: "supervised_count", headerName: t("supervised_count"), width: 160 },
    { field: "count", headerName: t("pig_count"), width: 160 },
  ];

  const applyPreset = (preset) => {
    setActivePreset(preset);
    const now = dayjs();
    switch (preset) {
      case "today":
        setDateRange([now.startOf("day"), now.endOf("day")]);
        break;
      case "last7":
        setDateRange([now.subtract(7, "day").startOf("day"), now.endOf("day")]);
        break;
      case "thisMonth":
        setDateRange([now.startOf("month"), now.endOf("month")]);
        break;
      case "last30":
        setDateRange([now.subtract(30, "day").startOf("day"), now.endOf("day")]);
        break;
      case "thisYear":
        setDateRange([now.startOf("year"), now.endOf("year")]);
        break;
      case "all":
      default:
        setDateRange([null, null]);
        break;
    }
  };

  const exportFilteredToCSV = () => {
    const headers = [
      "id", t("camera"), t("start"), t("end"), t("lote_number"),
      t("exit_room"), t("trip_number"), t("pig_sex"),
      t("supervised_count"), t("pig_count") 
    ];

    const rows = filteredRegistries.map(r => ([
      r.id,
      (cameraMap[r.camera_id] || `Camera ${r.camera_id ?? "-"}`).toString().replace(/,/g, " "),
      r.start_date ?? "",
      r.end_date ?? "",
      r.lote_number ?? "",
      r.exit_room ?? "",
      r.trip_number ?? "",
      (r.pig_sex ?? "").toString().toUpperCase(),
      Number.isFinite(+r.supervised_count) ? +r.supervised_count : "",
      Number.isFinite(+r.count) ? +r.count : ""      
    ]));

    const csv = [headers, ...rows].map(arr => arr.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const from = dateRange[0] ? dayjs(dateRange[0]).format("YYYYMMDD") : "ALL";
    const to = dateRange[1] ? dayjs(dateRange[1]).format("YYYYMMDD") : "ALL";
    a.download = `registries_${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", px: 0 }}>
      {/* Hero Image */}
      <Card sx={{ mb: 4, overflow: "hidden" }}>
        <CardMedia
          component="img"
          sx={{ height: { xs: 140, md: 200 }, objectFit: "cover" }}
          image={cerdodelosllanosCover}
          alt="Client Cover"
        />
      </Card>

      {/* Main Stats - Improved Cards (100% width, one line) */}
      <Grid container spacing={3} mb={3} alignItems="stretch">
        <Grid item xs={12} md={2}>
          <StatCard
            title={t("total_cameras")}
            value={fmt(counts.cameras)}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            title={t("total_users")}
            value={fmt(counts.users)}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            title={t("total_registries")}
            value={fmt(counts.registries)}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            title={t("avg_ai_accuracy") || "Precisión IA"}
            value={`${fmt(kpis.accuracy)}%`}
            subtitle={t("vs_supervised") || "vs supervisado"}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            title={t("weaned_last_7d") || "Últimos 7 días"}
            value={fmt(kpis.total7)}
            subtitle={t("weaned_pigs") || "cerdos destetados"}
            sx={{ height: '100%' }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            title={t("active_cameras_7d") || "Cámaras activas"}
            value={fmt(kpis.cams7)}
            subtitle={t("unique_cameras_reporting") || "con registros"}
            sx={{ height: '100%' }}
          />
        </Grid>
      </Grid>

      {/* Filters Section - Improved Layout */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {t("filters") || "Filtros"}
        </Typography>
        
        <Stack spacing={3}>
          {/* Quick Presets */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("quick_filters") || "Filtros rápidos"}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip 
                label={t("today") || "Hoy"} 
                onClick={() => applyPreset("today")}
                color={activePreset === "today" ? "primary" : "default"}
                variant={activePreset === "today" ? "filled" : "outlined"}
              />
              <Chip 
                label={t("last_7_days") || "Últimos 7"} 
                onClick={() => applyPreset("last7")}
                color={activePreset === "last7" ? "primary" : "default"}
                variant={activePreset === "last7" ? "filled" : "outlined"}
              />
              <Chip 
                label={t("this_month") || "Este mes"} 
                onClick={() => applyPreset("thisMonth")}
                color={activePreset === "thisMonth" ? "primary" : "default"}
                variant={activePreset === "thisMonth" ? "filled" : "outlined"}
              />
              <Chip 
                label={t("last_30_days") || "Últimos 30"} 
                onClick={() => applyPreset("last30")}
                color={activePreset === "last30" ? "primary" : "default"}
                variant={activePreset === "last30" ? "filled" : "outlined"}
              />
              <Chip 
                label={t("this_year") || "Este año"} 
                onClick={() => applyPreset("thisYear")}
                color={activePreset === "thisYear" ? "primary" : "default"}
                variant={activePreset === "thisYear" ? "filled" : "outlined"}
              />
              <Chip 
                label={t("all") || "Todo"} 
                onClick={() => applyPreset("all")}
                color={activePreset === "all" ? "primary" : "default"}
                variant={activePreset === "all" ? "filled" : "outlined"}
              />
            </Stack>
          </Box>

          <Divider />

          {/* Custom Date Range */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("custom_range") || "Rango personalizado"}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <DatePicker
                  label={t("from_date") || "Desde"}
                  value={dateRange[0]}
                  onChange={(newValue) => {
                    setDateRange([newValue, dateRange[1]]);
                    setActivePreset(null);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("to_date") || "Hasta"}
                  value={dateRange[1]}
                  onChange={(newValue) => {
                    setDateRange([dateRange[0], newValue]);
                    setActivePreset(null);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
            </LocalizationProvider>
          </Box>

          <Divider />

          {/* Group By */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("group_by") || "Agrupar por"}
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={groupBy}
              onChange={(_, val) => val && setGroupBy(val)}
              size="small"
              fullWidth
              sx={{ maxWidth: 300 }}
            >
              <ToggleButton value="day">{t("by_day") || "Día"}</ToggleButton>
              <ToggleButton value="week">{t("by_week") || "Semana"}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </Paper>

      {/* Charts */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mb: 4,
          width: "100%",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("weaned_per_period") ||
                  (groupBy === "week"
                    ? "Destetados por semana"
                    : "Destetados por día")}
              </Typography>
              <Box sx={{ width: "100%", height: 400 }}>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={dailyCounts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="ai_count"
                      name={t("ai_count") || "IA"}
                      stroke="#1976d2"
                      strokeWidth={2}
                    />
                    <Line
                      type="monotone"
                      dataKey="supervised_count"
                      name={t("supervised_count") || "Supervisado"}
                      stroke="#9c27b0"
                      strokeWidth={2}
                    />
                    {hasAnyManual && (
                      <Line
                        type="monotone"
                        dataKey="manual_count"
                        name={t("manual_count") || "Manual"}
                        stroke="#2e7d32"
                        strokeWidth={2}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("categories_per_period") ||
                  (groupBy === "week"
                    ? "Categorías por semana"
                    : "Categorías por día")}
              </Typography>
              <Box sx={{ width: "100%", height: 400 }}>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={categoryCounts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {categoryKeys.map((k, idx) => (
                      <Bar
                        key={k}
                        dataKey={k}
                        stackId="cats"
                        name={k}
                        fill={
                          [
                            "#1976d2",
                            "#9c27b0",
                            "#2e7d32",
                            "#ff9800",
                            "#6d4c41",
                            "#0288d1",
                            "#f06292",
                            "#7cb342",
                          ][idx % 8]
                        }
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button variant="contained" onClick={exportFilteredToCSV}>
          {t("export_csv") || "Exportar CSV"}
        </Button>
      </Box>
      <Typography variant="h5" gutterBottom>
        {t("recent_registries")}
      </Typography>
      <Box sx={{ width: "100%", height: gridHeight }}>
        <DataGrid
          rows={filteredRegistries.map((r) => ({ ...r, id: r.id }))}
          columns={registryColumns}
          disableColumnMenu={isXs}
          density={isXs ? "compact" : "standard"}
          initialState={{
            pagination: { paginationModel: { pageSize: isXs ? 5 : 10 } },
          }}
          pageSizeOptions={[5, 10]}
          disableSelectionOnClick
        />
      </Box>
    </Box>
  );
}
